import { AppWindow, Server } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { SectionProps } from "./common";
import HelpInfo from "@/components/HelpInfo";
import MultiValueTextFieldGroup from "@/components/MultiValueTextFieldGroup";
import BasicSelector from "@/components/Redux/BasicSelector";
import { ReduxHiddenDataField } from "@/components/Redux/ReduxHiddenDataField";
import StringMapInput from "@/components/Redux/StringMapInput";
import { ReduxTextField } from "@/components/Redux/TextField";
import SettingSection from "@/components/SettingSection";
import TabSection from "@/components/styled/TabSection";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { useSettings } from "@/hooks/use-settings";
import { cn } from "@/lib/utils";
import {
  getRouteFormProperty,
  setRouteFormError,
  setRouteFormProperties,
  setRouteFormProperty,
} from "@/slices/routes";
import { useDispatch, useSelector } from "@/store";
import { isDeepEmpty } from "@/utils/obj";
import { validateInteger } from "@/validators/integer";

export default function MCP({ readonly, tabValue }: SectionProps) {
  const dispatch = useDispatch();
  const settings = useSettings();
  const form = useSelector((state) => state.routes.form);

  const category = useMemo(() => {
    return form?.mcp?.client ? "client" : "server";
  }, [form]);

  function setCategory(category: "server" | "client") {
    switch (category) {
      // by default either Server or Client has to be set on MCP
      case "server": {
        dispatch(
          setRouteFormProperties([
            { path: "mcp.client", value: "" },
            { path: "mcp.server", value: {} },
          ]),
        );
        break;
      }
      case "client": {
        dispatch(
          setRouteFormProperties([
            { path: "mcp.server", value: "" },
            { path: "mcp.client", value: {} },
          ]),
        );
        break;
      }
    }
  }

  // When the MCP section first renders for a route that has no MCP data,
  // initialise the form to "server" so the UI selection matches the form state.
  useEffect(() => {
    if (readonly) {
      return;
    }
    if (!form?.mcp || (!form.mcp.client && !form.mcp.server)) {
      setCategory("server");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <TabSection index="mcp" value={tabValue} title="MCP">
      <RadioGroup
        aria-label="Select MCP configuration type"
        defaultValue="server"
        value={category}
        onValueChange={(value: string) =>
          setCategory(value as "server" | "client")
        }
        className="w-full grid grid-cols-1 md:grid-cols-[repeat(auto-fit,minmax(120px,max-content))] gap-2"
      >
        <div
          key="serverOptKey"
          className={cn(
            "relative flex flex-row gap-2 border rounded-lg px-3 py-2",
            category === "server" && "border-primary",
          )}
        >
          <RadioGroupItem id="serverOption" value="server" className="peer" />
          <Label htmlFor="serverOption">
            <Server className="size-3" />
            Server
          </Label>
        </div>

        <div
          key="clientOptkey"
          className={cn(
            "relative flex flex-row gap-2 border rounded-lg px-3 py-2",
            category === "client" && "border-primary",
          )}
        >
          <RadioGroupItem id="clientOption" value="client" className="peer" />
          <Label htmlFor="clientOption">
            <AppWindow className="size-3" />
            Client
          </Label>
        </div>
      </RadioGroup>

      {category === "server" && <MCPServerTab readonly={readonly} />}

      {category === "client" && (
        <>
          <h3
            className={`text-xl my-4 font-space-grotesk ${settings.paletteMode === "dark" ? "text-brand-tennis" : "text-brand-purple"} font-bold`}
          >
            CLIENT PROPERTIES
          </h3>
          <p>
            Pomerium will expose additional endpoints to manage MCP server
            discovery and upstream OAuth flows. See{" "}
            <a
              href="https://main.docs.pomerium.com/docs/capabilities/mcp#3-building-mcp-enabled-applications"
              className={cn(
                settings.paletteMode === "dark"
                  ? "text-brand-tennis"
                  : "text-brand-purple",
                "hover:opacity-85",
              )}
            >
              Building MCP Enabled Applications
            </a>{" "}
            for architectural details.
          </p>
        </>
      )}
    </TabSection>
  );
}

function MCPServerTab({ readonly }: { readonly: boolean }) {
  const dispatch = useDispatch();
  const settings = useSettings();
  const { form, formErrors } = useSelector((state) => state.routes);
  const [isUpstreamTokenRequired, setIsUpstreamTokenRequired] = useState(
    !!form?.mcp?.server?.upstreamOAuth2,
  );

  const AUTH_STYLE_OPTIONS = [
    // Unspecified = default value if empty
    { id: "OAUTH2_AUTH_STYLE_UNSPECIFIED", label: "Unspecified" },
    { id: "OAUTH2_AUTH_STYLE_IN_PARAMS", label: "In parameters" },
    { id: "OAUTH2_AUTH_STYLE_IN_HEADER", label: "In header" },
  ];

  const hasMCPServerFormErrors = useMemo(() => {
    return !isDeepEmpty(Object(formErrors).mcp?.server);
  }, [formErrors]);

  useEffect(() => {
    if (!isUpstreamTokenRequired) {
      if (hasMCPServerFormErrors) {
        dispatch(setRouteFormError({ path: "mcp", error: "" }));
      }
      dispatch(
        setRouteFormProperty({
          path: "mcp.server.upstreamOAuth2",
          value: undefined,
        }),
      );
    }
  }, [isUpstreamTokenRequired]);

  return (
    <>
      <div className="flex items-center pt-4 my-4 gap-4">
        <h3
          className={`text-xl my-4 font-space-grotesk uppercase ${settings.paletteMode === "dark" ? "text-brand-tennis" : "text-brand-purple"} font-bold flex items-center gap-1`}
        >
          <span>Upstream OAUTH2 Properties</span>
          <HelpInfo docsKey="mcp-server-upstream-oauth2" />
        </h3>
        <Switch
          aria-label="Enable upstream OAuth2 Properties"
          checked={isUpstreamTokenRequired}
          defaultChecked={false}
          onCheckedChange={(value) => setIsUpstreamTokenRequired(value)}
        />
      </div>

      {isUpstreamTokenRequired && (
        <>
          <SettingSection
            label="Client ID"
            docsKey="mcp-server-upstream-oauth2-client-id"
          >
            <ReduxTextField
              required={isUpstreamTokenRequired}
              disabled={readonly}
              fullWidth
              path="mcp.server.upstreamOAuth2.clientId"
              setter={setRouteFormProperty}
              getter={getRouteFormProperty}
              errorHandler={setRouteFormError}
            />
          </SettingSection>
          <SettingSection
            label="Client Secret"
            docsKey="mcp-server-upstream-oauth2-client-secret"
          >
            <ReduxHiddenDataField
              required={isUpstreamTokenRequired}
              disabled={readonly}
              fullWidth
              path="mcp.server.upstreamOAuth2.clientSecret"
              setter={setRouteFormProperty}
              getter={getRouteFormProperty}
              errorHandler={setRouteFormError}
            />
          </SettingSection>
          <SettingSection
            label="Authentication URL"
            docsKey="mcp-server-upstream-oauth2-auth-url"
          >
            <ReduxTextField
              disabled={readonly}
              fullWidth
              path="mcp.server.upstreamOAuth2.oauth2Endpoint.authUrl"
              placeholder="Optional — discovered automatically if left empty"
              setter={setRouteFormProperty}
              getter={getRouteFormProperty}
              errorHandler={setRouteFormError}
            />
          </SettingSection>
          <SettingSection
            label="Token URL"
            docsKey="mcp-server-upstream-oauth2-token-url"
          >
            <ReduxTextField
              disabled={readonly}
              fullWidth
              path="mcp.server.upstreamOAuth2.oauth2Endpoint.tokenUrl"
              placeholder="Optional — discovered automatically if left empty"
              setter={setRouteFormProperty}
              getter={getRouteFormProperty}
              errorHandler={setRouteFormError}
            />
          </SettingSection>
          <SettingSection
            label="Authentication Style"
            docsKey="mcp-server-upstream-oauth2-auth-style"
          >
            <BasicSelector
              readonly={readonly}
              options={AUTH_STYLE_OPTIONS}
              defaultValue={"OAUTH2_AUTH_STYLE_UNSPECIFIED"}
              path="mcp.server.upstreamOAuth2.oauth2Endpoint.authStyle"
              setter={setRouteFormProperty}
              getter={getRouteFormProperty}
              errorHandler={setRouteFormError}
            />
          </SettingSection>
          <SettingSection
            label="OAuth Scopes"
            docsKey="mcp-server-upstream-oauth2-scopes"
          >
            <MultiValueTextFieldGroup
              disabled={readonly}
              values={form?.mcp?.server?.upstreamOAuth2?.scopes || []}
              onSave={(values: string[]) =>
                dispatch(
                  setRouteFormProperty({
                    path: "mcp.server.upstreamOAuth2.scopes",
                    value: values.length === 0 ? undefined : values,
                  }),
                )
              }
            />
          </SettingSection>
          <SettingSection
            label="Authorization URL Params"
            docsKey="mcp-server-upstream-oauth2-authorization-url-params"
          >
            <StringMapInput
              readonly={readonly}
              path="mcp.server.upstreamOAuth2.authorizationUrlParams"
              setter={setRouteFormProperty}
              getter={getRouteFormProperty}
            />
          </SettingSection>
        </>
      )}
      <Separator />
      <SettingSection
        label="Authorization Server URL"
        docsKey="mcp-server-authorization-server-url"
      >
        <ReduxTextField
          disabled={readonly}
          fullWidth
          path="mcp.server.authorizationServerUrl"
          setter={setRouteFormProperty}
          getter={getRouteFormProperty}
          errorHandler={setRouteFormError}
        />
      </SettingSection>
      <SettingSection label="Path" docsKey="mcp-server-path">
        <ReduxTextField
          disabled={readonly}
          fullWidth
          path="mcp.server.path"
          setter={setRouteFormProperty}
          getter={getRouteFormProperty}
          errorHandler={setRouteFormError}
        />
      </SettingSection>
      <SettingSection
        label="Max Request Bytes"
        docsKey="mcp-server-max-request-bytes"
      >
        <ReduxTextField
          disabled={readonly}
          path="mcp.server.maxRequestBytes"
          setter={setRouteFormProperty}
          getter={getRouteFormProperty}
          errorHandler={setRouteFormError}
          validator={validateInteger}
        />
      </SettingSection>
    </>
  );
}
